import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../../../ui/Icon";
import { Art } from "../../../ui/Art";
import {
  clipFor as msClip, enClipFor, belonQuestion, pancaOptions, padanDeck, scramble, sortRound,
  ejaFor, padanOptFor, pancaFor, sortTopicsFor, EJA_ROUND, PADAN_PAIRS,
  type BelonMode, type Deria, type PadanCard, type PadanMode, type SortTopic,
} from "@jomcerdik/shared";
import { t, useLang } from "../../../lib/i18n";
import { speak } from "../../../lib/speak";
import { playClip, useAudioSource } from "../../../lib/bacaAudio";
import { Card, Screen, StudyGate, TopBar } from "../kit";
import { Burst, shuffle } from "../games/common";
import { Done, contentLang, isEn, useHear, useRecord } from "./TadikaPages";

/* More Tadika games: Pecah Belon, Asingkan Sains, Eja Perkataan, Padan Kad.
   Content is Bahasa Melayu by design; only the UI chrome is translated. Pictures are drawn art, never emoji. */

const word = (text: string) => (isEn() ? enClipFor("word", text) : msClip("word", text));
const RIGHT = () => (isEn() ? "Right! Well done!" : "Betul! Pandai!");
const TRY = () => (isEn() ? "Try again." : "Cuba lagi.");
const BETUL = () => (isEn() ? enClipFor("line", RIGHT()) : msClip("line", RIGHT()));
const CUBA = () => (isEn() ? enClipFor("line", TRY()) : msClip("line", TRY()));
const sayRight = () => playClip(BETUL(), RIGHT());
const sayTryAgain = () => playClip(CUBA(), TRY());
/** Read a question (line) or a word aloud: our English clip in English, the device's Malay voice in BM. */
const sayLine = (text: string, rate = 0.75) => { if (isEn()) playClip(enClipFor("line", text), text, rate); else speak(text, rate); };
const sayText = (text: string, rate = 0.7) => { if (isEn()) playClip(enClipFor("word", text), text, rate); else speak(text, rate); };
const NEXT = "w-full rounded-full bg-violet-500 py-3 text-lg font-extrabold text-white shadow-md";
const TONES = ["from-rose-400 to-pink-500", "from-amber-300 to-orange-400", "from-emerald-400 to-green-500", "from-sky-400 to-blue-500"];

function Counter({ i, max, score }: { i: number; max: number; score: number }) {
  return (
    <div className="flex items-center justify-between text-xs font-bold text-slate-400">
      <span>{Math.min(i + 1, max)}/{max}</span>
      <span className="inline-flex items-center gap-1"><Icon name="star" className="h-4 w-4 text-amber-400" />{score}</span>
    </div>
  );
}

function HearButton({ onClick }: { onClick: () => void }) {
  const hear = useHear();
  if (!hear) return null;
  return <button type="button" onClick={onClick} aria-label={t("Dengar")} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500 text-white"><Icon name="speaker" className="h-4 w-4" /></button>;
}

/* ── Pecah Belon (maths) ─────────────────────────────────────────────────── */
const BELON_ROUNDS = 10;
const BELON_LIVES = 3;
const BALLOON_TONES = ["from-rose-300 to-rose-500", "from-amber-200 to-orange-400", "from-emerald-300 to-green-500", "from-sky-300 to-blue-500"];

export function BelonPage() {
  return <Screen><StudyGate><Belon /></StudyGate></Screen>;
}

function Belon() {
  const [mode, setMode] = useState<BelonMode | null>(null);
  const [seed, setSeed] = useState(0);
  useAudioSource(useMemo(() => [BETUL(), CUBA()], []));
  const modes: { id: BelonMode; label: string; sign: string; tone: string }[] = [
    { id: "tambah", label: t("Tambah"), sign: "+", tone: "from-emerald-400 to-teal-500" },
    { id: "tolak", label: t("Tolak"), sign: "−", tone: "from-sky-400 to-blue-500" },
    { id: "campur", label: t("Campur"), sign: "±", tone: "from-amber-300 to-orange-400" },
  ];
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Pecah Belon")} subtitle={t("Tekan belon yang ada jawapan betul.")} />
      {mode === null ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {modes.map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`rounded-3xl bg-gradient-to-br ${m.tone} p-6 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
              <p className="text-6xl font-black">{m.sign}</p>
              <p className="mt-2 text-2xl font-extrabold">{m.label}</p>
              <p className="text-sm font-bold opacity-90">1 – 10</p>
            </button>
          ))}
        </div>
      ) : (
        <BelonGame key={seed} mode={mode} onAgain={() => setSeed((x) => x + 1)} onDone={() => setMode(null)} />
      )}
    </>
  );
}

function BelonGame({ mode, onAgain, onDone }: { mode: BelonMode; onAgain: () => void; onDone: () => void }) {
  const qs = useMemo(() => Array.from({ length: BELON_ROUNDS }, () => belonQuestion(mode, Math.random, contentLang())), [mode]);
  const [i, setI] = useState(0);
  const [lives, setLives] = useState(BELON_LIVES);
  const [score, setScore] = useState(0);
  const [popped, setPopped] = useState<number[]>([]);
  const [solved, setSolved] = useState(false);
  const [burst, setBurst] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const record = useRecord(0, `belon-${mode}`);
  const q = qs[i];
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (q) sayLine(q.spoken); }, [i]);

  if (!q || lives <= 0) return <Done score={score} max={qs.length} onAgain={onAgain} onDone={onDone} save={record} />;

  const advance = (ms: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setI((x) => x + 1); setPopped([]); setSolved(false); }, ms);
  };
  function pick(n: number, k: number) {
    if (solved || popped.includes(k) || !q) return;
    if (n === q.answer) {
      setSolved(true); setScore((s) => s + 1); setBurst((b) => b + 1); sayRight(); advance(900);
    } else {
      setPopped((p) => [...p, k]); setLives((l) => l - 1); sayTryAgain();
    }
  }
  /** The right balloon floated away: lose a life and move on. */
  function missed() {
    if (solved) return;
    setSolved(true); setLives((l) => l - 1); sayTryAgain(); advance(500);
  }
  const duration = Math.max(6, 11 - i * 0.4);

  return (
    <Card className="space-y-3 text-center">
      <Burst show={burst} />
      <div className="flex items-center justify-between">
        <Counter i={i} max={qs.length} score={score} />
        <span className="flex gap-0.5" role="img" aria-label={t("Nyawa: {n}", { n: lives })}>
          {Array.from({ length: BELON_LIVES }, (_, k) => <Icon key={k} name="heart" className={`h-6 w-6 ${k < lives ? "text-rose-400" : "text-slate-300"}`} />)}
        </span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <p className="text-4xl font-black text-slate-900">{q.text} = ?</p>
        <HearButton onClick={() => sayLine(q.spoken)} />
      </div>
      <div className="relative h-[26rem] overflow-hidden rounded-3xl bg-gradient-to-b from-sky-200 via-sky-50 to-emerald-100 ring-1 ring-sky-100">
        {q.options.map((n, k) => !popped.includes(k) && (
          <button key={`${i}-${k}`} type="button" onClick={() => pick(n, k)} onAnimationEnd={n === q.answer ? missed : undefined}
            aria-label={String(n)}
            className={`absolute bottom-0 grid h-[5.5rem] w-[4.5rem] animate-belon place-items-center rounded-[50%_50%_46%_46%] bg-gradient-to-br ${BALLOON_TONES[k % 4]} text-3xl font-black text-white shadow-lg ${solved && n !== q.answer ? "opacity-0" : ""}`}
            style={{ left: `${6 + k * 24}%`, animationDuration: `${duration}s`, animationDelay: `${k * 0.3}s`, animationPlayState: solved ? "paused" : "running" }}>
            <span className="drop-shadow">{n}</span>
            <span aria-hidden className="absolute left-1/2 top-full h-8 w-px bg-slate-400/70" />
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ── Asingkan Sains ──────────────────────────────────────────────────────── */
type SainsPick = { kind: "sort"; topic: SortTopic } | { kind: "panca" } | null;

export function SainsPage() {
  return <Screen><StudyGate><Sains /></StudyGate></Screen>;
}

function Sains() {
  const [pick, setPick] = useState<SainsPick>(null);
  const [seed, setSeed] = useState(0);
  const appLang = useLang();
  const SORT_TOPICS = useMemo(() => sortTopicsFor(contentLang()), [appLang]);
  useAudioSource(useMemo(() => [BETUL(), CUBA()], [appLang]));
  const again = () => setSeed((x) => x + 1);
  const back = () => setPick(null);
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Asingkan Sains")} subtitle={t("Pilih topik, kemudian jawab dengan gambar.")} />
      {pick === null ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SORT_TOPICS.map((tp, n) => (
            <button key={tp.id} type="button" onClick={() => setPick({ kind: "sort", topic: tp })}
              className={`rounded-3xl bg-gradient-to-br ${TONES[n % 4]} p-4 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
              <Art id={tp.items[0]!.art} alt="" className="mx-auto h-20 w-20" />
              <span className="mt-1 block text-base font-extrabold leading-tight">{tp.title}</span>
            </button>
          ))}
          <button type="button" onClick={() => setPick({ kind: "panca" })} className={`rounded-3xl bg-gradient-to-br ${TONES[3]} p-4 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
            <Art id="badan/mata" alt="" className="mx-auto h-20 w-20" />
            <span className="mt-1 block text-base font-extrabold leading-tight">{isEn() ? "The Five Senses" : "Pancaindera"}</span>
          </button>
        </div>
      ) : pick.kind === "sort" ? (
        <SortGame key={`${pick.topic.id}-${seed}`} topic={pick.topic} onAgain={again} onDone={back} />
      ) : (
        <PancaGame key={`panca-${seed}`} onAgain={again} onDone={back} />
      )}
    </>
  );
}

function SortGame({ topic, onAgain, onDone }: { topic: SortTopic; onAgain: () => void; onDone: () => void }) {
  const items = useMemo(() => sortRound(topic), [topic]);
  const [i, setI] = useState(0);
  const [wrong, setWrong] = useState<number[]>([]);
  const [solved, setSolved] = useState(false);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const record = useRecord(0, `sains-${topic.id}`);
  const it = items[i];
  useEffect(() => { if (it) sayText(it.name); }, [i]);
  if (!it) return <Done score={score} max={items.length} onAgain={onAgain} onDone={onDone} save={record} />;

  function pick(b: number) {
    if (solved || !it) return;
    if (b === it.to) {
      setSolved(true); setBurst((n) => n + 1); if (!wrong.length) setScore((s) => s + 1); sayRight();
    } else { setWrong((w) => [...w, b]); sayTryAgain(); }
  }
  function next() { setI((x) => x + 1); setWrong([]); setSolved(false); }

  return (
    <Card className="space-y-4 text-center">
      <Burst show={burst} />
      <Counter i={i} max={items.length} score={score} />
      <p className="text-xl font-extrabold text-slate-900">{topic.title}</p>
      <div key={i} className="mx-auto w-fit animate-rise rounded-3xl bg-gradient-to-b from-sky-50 to-white p-4 ring-1 ring-sky-100 dark:from-slate-100 dark:to-slate-50">
        <Art id={it.art} alt={it.name} className="mx-auto h-36 w-36" />
        <div className="mt-1 flex items-center justify-center gap-2">
          <p className="text-2xl font-black text-slate-900">{it.name}</p>
          <HearButton onClick={() => sayText(it.name)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {topic.buckets.map((b, k) => {
          const st = solved ? (k === it.to ? "right" : "dim") : wrong.includes(k) ? "wrong" : "idle";
          return (
            <button key={b.name} type="button" onClick={() => pick(k)}
              className={`rounded-3xl bg-gradient-to-br ${b.tone} py-6 text-2xl font-black text-white shadow-md ring-4 transition active:scale-95
                ${st === "right" ? "ring-emerald-300 animate-tap" : st === "wrong" ? "ring-rose-300 animate-shake opacity-70" : st === "dim" ? "opacity-40 ring-transparent" : "ring-white/40"}`}>
              {b.name}
            </button>
          );
        })}
      </div>
      {solved && <button type="button" onClick={next} className={NEXT}>{t("Seterusnya")} ›</button>}
    </Card>
  );
}

function PancaGame({ onAgain, onDone }: { onAgain: () => void; onDone: () => void }) {
  const list = useMemo(() => pancaFor(contentLang()), []);
  const deck = useMemo(() => shuffle(list), [list]);
  const [i, setI] = useState(0);
  const [wrong, setWrong] = useState<string[]>([]);
  const [solved, setSolved] = useState(false);
  const [score, setScore] = useState(0);
  const [burst, setBurst] = useState(0);
  const record = useRecord(0, "sains-pancaindera");
  const cur = deck[i];
  const opts = useMemo<Deria[]>(() => (cur ? pancaOptions(cur, Math.random, list) : []), [cur, list]);
  useEffect(() => { if (cur) sayLine(cur.question); }, [i]);
  if (!cur) return <Done score={score} max={deck.length} onAgain={onAgain} onDone={onDone} save={record} />;

  function pick(d: Deria) {
    if (solved || !cur) return;
    if (d.part === cur.part) {
      setSolved(true); setBurst((n) => n + 1); if (!wrong.length) setScore((s) => s + 1); sayRight();
    } else { setWrong((w) => [...w, d.part]); sayTryAgain(); }
  }
  function next() { setI((x) => x + 1); setWrong([]); setSolved(false); }

  return (
    <Card className="space-y-4 text-center">
      <Burst show={burst} />
      <Counter i={i} max={deck.length} score={score} />
      <div className="flex items-center justify-center gap-2">
        <p className="text-2xl font-extrabold text-slate-900">{cur.question}</p>
        <HearButton onClick={() => sayLine(cur.question)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {opts.map((d, k) => {
          const st = solved ? (d.part === cur.part ? "right" : "dim") : wrong.includes(d.part) ? "wrong" : "idle";
          return (
            <button key={d.part} type="button" onClick={() => pick(d)}
              className={`rounded-3xl bg-gradient-to-br ${TONES[k % 4]} p-3 text-white shadow-md ring-4 transition active:scale-95
                ${st === "right" ? "ring-emerald-300 animate-tap" : st === "wrong" ? "ring-rose-300 animate-shake opacity-70" : st === "dim" ? "opacity-40 ring-transparent" : "ring-white/40"}`}>
              <Art id={d.art} alt="" className="mx-auto h-24 w-24 rounded-2xl bg-white/80" />
              <span className="mt-1 block text-xl font-black">{d.part}</span>
            </button>
          );
        })}
      </div>
      {solved && <button type="button" onClick={next} className={NEXT}>{t("Seterusnya")} ›</button>}
    </Card>
  );
}

/* ── Eja Perkataan ───────────────────────────────────────────────────────── */
export function EjaPage() {
  return <Screen><StudyGate><Eja /></StudyGate></Screen>;
}

function Eja() {
  const [seed, setSeed] = useState(0);
  const [started, setStarted] = useState(false);
  const appLang = useLang();
  useAudioSource(useMemo(() => [BETUL(), CUBA(), ...ejaFor(contentLang()).map((x) => word(x.w))], [appLang]));
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Eja Perkataan")} subtitle={t("Lihat gambar, kemudian susun huruf.")} />
      {started ? (
        <EjaGame key={`${seed}${appLang}`} onAgain={() => setSeed((x) => x + 1)} onDone={() => setStarted(false)} />
      ) : (
        <Card className="space-y-4 text-center">
          <Art id={isEn() ? "buah/epal" : "huruf/huruf-a"} alt="" className="mx-auto h-36 w-36" />
          <button type="button" onClick={() => setStarted(true)} className="rounded-full bg-emerald-500 px-10 py-3 text-xl font-extrabold text-white shadow-md">{t("Mula")} ›</button>
        </Card>
      )}
    </>
  );
}

type Tile = { id: number; ch: string; used: boolean };

function EjaGame({ onAgain, onDone }: { onAgain: () => void; onDone: () => void }) {
  const words = useMemo(() => shuffle(ejaFor(contentLang())).slice(0, EJA_ROUND), []);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const record = useRecord(0, "eja");
  const w = words[i];
  if (!w) return <Done score={score} max={words.length} onAgain={onAgain} onDone={onDone} save={record} />;
  // One component per word (keyed), so tiles, slots and the picture always change together.
  return <EjaRound key={`${i}-${w.w}`} w={w.w} art={w.art} i={i} total={words.length} score={score} onNext={(clean) => { if (clean) setScore((s) => s + 1); setI((x) => x + 1); }} />;
}

function EjaRound({ w, art, i, total, score, onNext }: { w: string; art: string; i: number; total: number; score: number; onNext: (clean: boolean) => void }) {
  const [tiles, setTiles] = useState<Tile[]>(() => scramble(w).map((ch, id) => ({ id, ch, used: false })));
  const [slots, setSlots] = useState<number[]>(() => Array.from({ length: w.length }, () => -1)); // tile ids in order, -1 = empty
  const [state, setState] = useState<"play" | "right" | "wrong">("play");
  const [missed, setMissed] = useState(false);
  const [burst, setBurst] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { playClip(word(w), w, 0.7); return () => clearTimeout(timer.current); }, [w]);

  function reset() { setTiles((ts) => ts.map((x) => ({ ...x, used: false }))); setSlots(Array.from({ length: w.length }, () => -1)); setState("play"); }
  function check(next: number[], ts: Tile[]) {
    const spelled = next.map((id) => ts.find((x) => x.id === id)!.ch).join("");
    if (spelled === w) {
      setState("right"); setBurst((b) => b + 1); playClip(word(w), w, 0.7);
    } else {
      setState("wrong"); setMissed(true); sayTryAgain();
      clearTimeout(timer.current);
      timer.current = setTimeout(reset, 900);
    }
  }
  function tapTile(tile: Tile) {
    if (state !== "play" || tile.used) return;
    const at = slots.indexOf(-1);
    if (at < 0) return;
    const next = slots.map((s, k) => (k === at ? tile.id : s));
    const ts = tiles.map((x) => (x.id === tile.id ? { ...x, used: true } : x));
    setSlots(next); setTiles(ts);
    if (!next.includes(-1)) check(next, ts);
  }
  function tapSlot(k: number) {
    const id = slots[k]!;
    if (state !== "play" || id < 0) return;
    setSlots((s) => s.map((v, j) => (j === k ? -1 : v)));
    setTiles((ts) => ts.map((x) => (x.id === id ? { ...x, used: false } : x)));
  }
  const letter = (id: number) => tiles.find((x) => x.id === id)?.ch.toUpperCase() ?? "";

  return (
    <Card className="space-y-4 text-center">
      <Burst show={burst} />
      <Counter i={i} max={total} score={score} />
      <div className="flex items-center justify-center gap-2">
        <Art id={art} alt="" className="h-36 w-36" />
        <HearButton onClick={() => playClip(word(w), w, 0.7)} />
      </div>
      <div className={`flex flex-wrap justify-center gap-2 ${state === "wrong" ? "animate-shake" : ""}`}>
        {slots.map((id, k) => (
          <button key={k} type="button" onClick={() => tapSlot(k)} aria-label={id >= 0 ? letter(id) : ""}
            className={`grid h-16 w-14 place-items-center rounded-2xl text-3xl font-black ${id >= 0 ? (state === "right" ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-400" : "bg-white text-pink-600 ring-2 ring-pink-300") : "border-4 border-dashed border-sky-300 bg-amber-50"}`}>
            {id >= 0 ? letter(id) : ""}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {tiles.map((x) => (
          <button key={x.id} type="button" onClick={() => tapTile(x)} disabled={x.used} aria-label={x.ch.toUpperCase()}
            className={`h-16 w-14 rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 text-3xl font-black text-white shadow-md active:scale-95 ${x.used ? "invisible" : ""}`}>
            {x.ch.toUpperCase()}
          </button>
        ))}
      </div>
      {state === "right" ? (
        <button type="button" onClick={() => onNext(!missed)} className={NEXT}>{t("Seterusnya")} ›</button>
      ) : (
        <button type="button" onClick={reset} className="rounded-full bg-card px-5 py-2 text-sm font-bold text-slate-700 ring-1 ring-black/10">{t("Padam")}</button>
      )}
    </Card>
  );
}

/* ── Padan Kad ───────────────────────────────────────────────────────────── */
export function TadikaPadanPage() {
  return <Screen><StudyGate><Padan /></StudyGate></Screen>;
}

function Padan() {
  const [mode, setMode] = useState<PadanMode | null>(null);
  const [seed, setSeed] = useState(0);
  const appLang = useLang();
  useAudioSource(useMemo(() => [BETUL(), CUBA(), ...ejaFor(contentLang()).map((x) => word(x.w))], [appLang]));
  const modes: { id: PadanMode; label: string; art: string; tone: string }[] = [
    { id: "huruf", label: t("Huruf besar dan kecil"), art: isEn() ? "buah/epal" : "huruf/huruf-a", tone: "from-amber-300 to-orange-400" },
    { id: "gambar", label: t("Gambar dan perkataan"), art: "baca/kucing", tone: "from-emerald-400 to-teal-500" },
  ];
  return (
    <>
      <TopBar back="/belajar/tadika" title={t("Padan Kad")} subtitle={t("Buka dua kad dan cari pasangan yang sama.")} />
      {mode === null ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {modes.map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`rounded-3xl bg-gradient-to-br ${m.tone} p-6 text-center text-white shadow-md transition hover:-translate-y-0.5`}>
              <Art id={m.art} alt="" className="mx-auto h-24 w-24" />
              <p className="mt-2 text-xl font-extrabold">{m.label}</p>
            </button>
          ))}
        </div>
      ) : (
        <PadanGame key={`${seed}${appLang}`} mode={mode} onAgain={() => setSeed((x) => x + 1)} onDone={() => setMode(null)} />
      )}
    </>
  );
}

function PadanGame({ mode, onAgain, onDone }: { mode: PadanMode; onAgain: () => void; onDone: () => void }) {
  const deck = useMemo<PadanCard[]>(() => padanDeck(mode, Math.random, padanOptFor(contentLang())), [mode]);
  const [open, setOpen] = useState<string[]>([]);
  const [done, setDone] = useState<string[]>([]); // matched pair keys
  const [moves, setMoves] = useState(0);
  const [burst, setBurst] = useState(0);
  const lock = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const record = useRecord(0, `padan-${mode}`);
  useEffect(() => () => clearTimeout(timer.current), []);
  const finished = done.length === PADAN_PAIRS;
  // Every pair found counts; each extra move beyond the minimum costs half a point.
  const score = Math.max(1, PADAN_PAIRS - Math.floor(Math.max(0, moves - PADAN_PAIRS) / 2));
  if (finished) return <Done score={score} max={PADAN_PAIRS} onAgain={onAgain} onDone={onDone} save={record} />;

  function say(c: PadanCard) {
    if (mode === "gambar") playClip(word(c.text), c.text, 0.7);
    else if (isEn()) playClip(enClipFor("line", `Letter ${c.text.toUpperCase()}.`), c.text, 0.7);
    else speak(c.text, 0.7);
  }
  function flip(c: PadanCard) {
    if (lock.current || open.includes(c.id) || done.includes(c.pair)) return;
    const now = [...open, c.id];
    setOpen(now); say(c);
    if (now.length < 2) return;
    setMoves((m) => m + 1);
    const [a, b] = now.map((id) => deck.find((x) => x.id === id)!);
    lock.current = true;
    timer.current = setTimeout(() => {
      if (a!.pair === b!.pair) { setDone((d) => [...d, a!.pair]); setBurst((n) => n + 1); sayRight(); }
      else sayTryAgain();
      setOpen([]); lock.current = false;
    }, a!.pair === b!.pair ? 600 : 1000);
  }

  return (
    <Card className="space-y-3">
      <Burst show={burst} />
      <div className="flex items-center justify-between text-xs font-bold text-slate-400">
        <span>{t("Langkah: {n}", { n: moves })}</span>
        <span className="inline-flex items-center gap-1"><Icon name="star" className="h-4 w-4 text-amber-400" />{done.length}/{PADAN_PAIRS}</span>
      </div>
      <div className="mx-auto grid max-w-md grid-cols-3 gap-2 sm:grid-cols-4">
        {deck.map((c) => {
          const shown = open.includes(c.id) || done.includes(c.pair);
          const matched = done.includes(c.pair);
          return (
            <button key={c.id} type="button" onClick={() => flip(c)} aria-label={shown ? c.text : "?"}
              className={`grid aspect-square place-items-center overflow-hidden rounded-2xl p-1 text-4xl font-black shadow-md transition active:scale-95
                ${matched ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-400" : shown ? "bg-white text-pink-600 ring-2 ring-pink-300" : "bg-gradient-to-br from-sky-400 to-blue-500 text-white"}`}>
              {!shown ? "?" : c.kind === "art" ? <Art id={c.art!} alt={c.text} className="h-full w-full" /> : <span className={mode === "gambar" ? "text-xl sm:text-2xl" : ""}>{c.text}</span>}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
